<template>
  <div class="detail-page">
    <Header />
    
    <main class="main-content">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="spinner-border text-emerald-600" role="status"></div>
        <p>Đang tải thông tin gian hàng sách cũ...</p>
      </div>

      <!-- Not Found State -->
      <div v-else-if="!booth" class="not-found-state">
        <icon icon="fa-solid fa-store-slash" class="not-found-icon" />
        <h3 class="font-serif">Không tìm thấy gian hàng</h3>
        <p>Gian hàng sách cũ này không tồn tại hoặc đã bị gỡ.</p>
        <router-link to="/old-books" class="btn-back-list">
          <icon icon="fa-solid fa-arrow-left" class="me-1" /> Quay lại chợ sách cũ
        </router-link>
      </div>

      <!-- Booth Detail Content -->
      <div v-else class="detail-container">
        <!-- Breadcrumb -->
        <nav class="breadcrumb-nav" aria-label="breadcrumb">
          <router-link to="/">Trang chủ</router-link>
          <span class="bc-sep">/</span>
          <router-link to="/old-books">Sách cũ</router-link>
          <span class="bc-sep">/</span>
          <span class="bc-current">{{ booth.name }}</span>
        </nav>

        <!-- Top Booth Overview Section -->
        <div class="booth-top-overview">
          
          <!-- Left: Media Gallery -->
          <div class="gallery-col">
            <div class="main-image-frame" @click="openZoomModal" title="Bấm để phóng to ảnh">
              <img :src="getCoverUrl(activeImage)" :alt="booth.name" class="main-image" @error="handleImageError" />
              
              <!-- Badges on Main Image (Top) -->
              <span v-if="activeKey === 'stack' && booth.stackImage" class="detail-figure-badge stack-badge">
                <icon icon="fa-solid fa-camera" class="me-1" />Ảnh chồng sách
              </span>
              <span v-else-if="activeBookItem" class="detail-figure-badge cond-badge" :class="'cond-' + activeBookItem.verStatus">
                {{ getConditionLabel(activeBookItem.verStatus) }} ({{ activeBookItem.perStatus }}% mới)
              </span>

              <!-- Label Badge on Main Image (Bottom Left) -->
              <span class="main-image-badge" v-if="activeImageLabel">
                <icon icon="fa-solid fa-image" class="me-1" /> {{ activeImageLabel }}
              </span>

              <!-- Zoom Hint Pill (Bottom Right) -->
              <div class="gallery-zoom-hint">
                <icon icon="fa-solid fa-magnifying-glass-plus" class="me-1" /> Phóng to ảnh
              </div>
            </div>

            <!-- Thumbnail Carousel / Strip with Navigation Arrows -->
            <div class="gallery-thumb-carousel">
              <button 
                type="button" 
                class="thumb-nav-btn btn-prev" 
                @click="scrollThumbs('left')" 
                title="Xem ảnh trước"
              >
                <icon icon="fa-solid fa-chevron-left" />
              </button>

              <div class="thumb-strip-scrollable" ref="thumbStrip" @wheel="handleThumbWheel">
                <!-- Stack Image Thumbnail if exists -->
                <button 
                  v-if="booth.stackImage"
                  type="button"
                  class="thumb-btn stack-thumb"
                  :class="{ active: activeKey === 'stack' }"
                  @click="setActiveMedia(booth.stackImage, null, 'stack')"
                  title="Ảnh chụp chồng sách"
                >
                  <div class="thumb-img-wrap">
                    <img :src="getCoverUrl(booth.stackImage)" alt="Chồng sách" @error="handleImageError" />
                  </div>
                  <span class="thumb-tag-badge stack-tag">Chồng sách</span>
                </button>

                <!-- Thumbs for each book -->
                <template v-for="(b, idx) in booth.books" :key="b.id || idx">
                  <button 
                    type="button"
                    class="thumb-btn"
                    :class="{ active: activeKey === 'book_' + idx + '_cover' }"
                    @click="setActiveMedia(b.cover, b, 'book_' + idx + '_cover')"
                    :title="b.name + ' - Bìa trước'"
                  >
                    <div class="thumb-img-wrap">
                      <img :src="getCoverUrl(b.cover)" :alt="b.name + ' - Bìa trước'" @error="handleImageError" />
                    </div>
                    <span class="thumb-tag-badge">C.{{ idx + 1 }} trước</span>
                  </button>

                  <button 
                    v-if="b.coverBack"
                    type="button"
                    class="thumb-btn"
                    :class="{ active: activeKey === 'book_' + idx + '_coverBack' }"
                    @click="setActiveMedia(b.coverBack, b, 'book_' + idx + '_coverBack')"
                    :title="b.name + ' - Bìa sau'"
                  >
                    <div class="thumb-img-wrap">
                      <img :src="getCoverUrl(b.coverBack)" :alt="b.name + ' - Bìa sau'" @error="handleImageError" />
                    </div>
                    <span class="thumb-tag-badge">C.{{ idx + 1 }} sau</span>
                  </button>
                </template>
              </div>

              <button 
                type="button" 
                class="thumb-nav-btn btn-next" 
                @click="scrollThumbs('right')" 
                title="Xem ảnh tiếp theo"
              >
                <icon icon="fa-solid fa-chevron-right" />
              </button>
            </div>
          </div>

          <!-- Right: Booth Main Info & Actions -->
          <div class="info-col">
            
            <!-- Owner Toolbar / Status Action -->
            <div v-if="isOwner" class="owner-toolbar">
              <div class="owner-toolbar-left">
                <span class="owner-status-pill">
                  <icon icon="fa-solid fa-user-shield" class="me-1 text-emerald-600" />
                  Gian hàng của bạn
                </span>
                <span class="owner-status-desc">
                  Bài đăng đang được hiển thị công khai trên Chợ sách cũ
                </span>
              </div>
              <div class="owner-toolbar-actions">
                <button 
                  type="button" 
                  class="btn-owner-edit" 
                  :disabled="booth.status === 2 || booth.status === 4"
                  @click="goToEdit"
                  :title="booth.status === 2 || booth.status === 4 ? 'Gian hàng đã bán hoặc đã hủy không thể chỉnh sửa' : 'Chỉnh sửa nội dung & ảnh gian hàng'"
                >
                  <icon icon="fa-solid fa-pen-to-square" class="me-1" />
                  <span>Chỉnh sửa bài đăng</span>
                </button>
                <router-link to="/my-old-books" class="btn-owner-manage">
                  <icon icon="fa-solid fa-list-check" class="me-1" />
                  <span>Kho sách của tôi</span>
                </router-link>
              </div>
            </div>

            <!-- Title -->
            <h1 class="booth-title font-serif">{{ booth.name }}</h1>

            <!-- Meta Badges Strip -->
            <div class="booth-meta-strip">
              <span class="meta-badge count-badge">
                <icon :icon="booth.totalBooks > 1 ? 'fa-solid fa-layer-group' : 'fa-solid fa-book'" class="me-1" />
                {{ booth.totalBooks > 1 ? ('Combo ' + booth.totalBooks + ' cuốn') : '1 cuốn sách' }}
              </span>

              <span class="meta-badge sale-type-badge" :class="booth.saleType === 1 ? 'type-combo' : 'type-single'">
                {{ booth.saleType === 1 ? 'Bắt buộc mua cả combo' : 'Cho phép mua lẻ từng cuốn' }}
              </span>

              <span v-if="booth.weight > 0" class="meta-badge weight-badge">
                <icon icon="fa-solid fa-weight-hanging" class="me-1 opacity-75" />
                Khối lượng: {{ formatWeight(booth.weight) }}
              </span>

              <span class="meta-badge status-badge" :class="'st-' + booth.status">
                <span class="dot"></span>
                {{ getListingStatusLabel(booth.status) }}
              </span>
            </div>

            <!-- Price & Sale Off Row -->
            <div class="price-box">
              <div class="price-box-main">
                <span class="price-box-label">
                  {{ booth.saleType === 1 ? 'Giá trọn gói Combo' : 'Tổng giá trị gian hàng' }}
                </span>
                <div class="d-flex align-items-baseline gap-3 mt-1">
                  <strong class="price-amount-hero">{{ formatPrice(booth.price) }}</strong>
                  
                  <span v-if="booth.originalPrice && booth.originalPrice > booth.price" class="price-original-strikethrough">
                    {{ formatPrice(booth.originalPrice) }}
                  </span>

                  <span v-if="booth.discountRate && booth.discountRate > 0" class="sale-off-hero-badge">
                    -{{ booth.discountRate }}% SALE OFF
                  </span>
                </div>
              </div>

              <div class="price-box-note" v-if="booth.discountRate && booth.discountRate > 0">
                <icon icon="fa-solid fa-circle-check" class="text-emerald-600 me-1" />
                Tiết kiệm được <strong>{{ formatPrice(booth.originalPrice - booth.price) }}</strong> so với giá bìa gốc!
              </div>
            </div>

            <!-- If 1 book: Specs & description of that book directly in the main overview -->
            <div class="single-book-overview-meta mt-3" v-if="booth.books && booth.books.length === 1">
              <div class="book-specs-strip">
                <div class="spec-pill" v-if="booth.books[0].author">
                  <span class="spec-lbl">Tác giả:</span> <strong>{{ booth.books[0].author }}</strong>
                </div>
                <div class="spec-pill">
                  <span class="spec-lbl">Độ mới:</span> <strong>{{ booth.books[0].perStatus }}%</strong>
                </div>
                <div class="spec-pill">
                  <span class="spec-lbl">Tình trạng:</span> <strong>{{ getConditionLabel(booth.books[0].verStatus) }}</strong>
                </div>
                <div class="spec-pill" v-if="booth.books[0].totalPage">
                  <span class="spec-lbl">Số trang:</span> <span>{{ booth.books[0].totalPage }}</span>
                </div>
              </div>

              <div class="preview-cat-tags mt-2" v-if="booth.books[0].categoryNames && booth.books[0].categoryNames.length > 0">
                <span v-for="(cat, cIdx) in booth.books[0].categoryNames" :key="'cat-' + cIdx" class="preview-cat-chip">
                  {{ cat }}
                </span>
              </div>

              <div class="booth-desc-section mt-3" v-if="booth.books[0].description">
                <h3 class="section-sub-title">Mô tả tình trạng sách</h3>
                <p class="desc-content">{{ booth.books[0].description }}</p>
              </div>
            </div>

            <!-- Description for combo / multiple books -->
            <div class="booth-desc-section" v-else-if="booth.description">
              <h3 class="section-sub-title">Ghi chú từ người bán</h3>
              <p class="desc-content">{{ booth.description }}</p>
            </div>

            <!-- Seller & Primary CTA Box -->
            <div class="seller-cta-box">
              <div class="seller-profile">
                <div class="seller-avatar-wrap">
                  <icon icon="fa-solid fa-store" />
                </div>
                <div class="seller-details">
                  <h4 class="seller-name">{{ booth.createByName || 'Người bán sách' }}</h4>
                  <span class="seller-date">Đăng ngày {{ formatDate(booth.createTime) }}</span>
                </div>
              </div>

              <button 
                type="button" 
                class="btn-primary-buy"
                :class="{ 'in-cart': isBoothInCart, 'is-mine': isMyBooth }"
                :disabled="booth.status === 2 || booth.status === 4 || isBoothInCart || isMyBooth"
                @click="handleBuyBooth"
              >
                <icon :icon="isMyBooth ? 'fa-solid fa-user-check' : (isBoothInCart ? 'fa-solid fa-check' : 'fa-solid fa-cart-shopping')" class="me-2" />
                <span>
                  {{ isMyBooth ? 'Gian hàng của bạn' : (isBoothInCart ? (booth.saleType === 1 ? 'Combo đã có trong giỏ' : 'Gian hàng đã có trong giỏ') : (booth.saleType === 1 ? 'Mua trọn bộ Combo' : 'Đặt mua gian hàng này')) }}
                </span>
              </button>
            </div>

          </div>
        </div>

        <!-- Section: Danh Sách Sách Trong Gian Hàng (chỉ khi có >= 2 cuốn) -->
        <section class="booth-books-list-section" v-if="booth.books && booth.books.length > 1">
          <div class="section-header-row">
            <div>
              <h2 class="section-main-title font-serif">
                <icon icon="fa-solid fa-boxes-stacked" class="text-emerald-600 me-2" />
                Chi tiết từng cuốn sách trong gian hàng
              </h2>
              <p class="section-main-subtitle">
                Xem hình ảnh thực tế bìa trước, bìa sau, độ mới và mô tả chi tiết của từng cuốn sách
              </p>
            </div>
            <span class="section-count-tag">
              Tổng số: <strong>{{ booth.books ? booth.books.length : 0 }}</strong> cuốn
            </span>
          </div>

          <!-- Batch Selection Control Toolbar -->
          <div class="books-selection-toolbar">
            <div class="selection-toolbar-left">
              <label class="custom-select-all-label" title="Chọn/bỏ chọn tất cả sách">
                <input 
                  type="checkbox" 
                  class="custom-check-native" 
                  :checked="isAllBooksSelected" 
                  @change="toggleSelectAllBooks" 
                />
                <span class="custom-check-box">
                  <icon v-if="isAllBooksSelected" icon="fa-solid fa-check" class="check-icon" />
                </span>
                <span class="check-text">
                  Chọn tất cả (<strong>{{ booth.books.length }}</strong> cuốn)
                </span>
              </label>

              <span class="selected-count-pill" v-if="selectedBooksCount > 0">
                Đã chọn <strong>{{ selectedBooksCount }}</strong> cuốn
              </span>
            </div>

            <div class="selection-toolbar-right" v-if="selectedBooksCount > 0">
              <span class="selection-price-summary">
                Tạm tính: <strong class="text-emerald-700">{{ formatPrice(selectedBooksTotalPrice) }}</strong>
              </span>

              <button 
                type="button" 
                class="btn-batch-add-cart" 
                :class="{ 'in-cart': isAllSelectedBooksInCart, 'is-mine': isMyBooth }"
                :disabled="booth.status === 2 || booth.status === 4 || isAllSelectedBooksInCart || isMyBooth"
                @click="handleAddSelectedBooksToCart"
              >
                <icon :icon="isMyBooth ? 'fa-solid fa-user-check' : (isAllSelectedBooksInCart ? 'fa-solid fa-check' : 'fa-solid fa-cart-plus')" class="me-1" />
                <span v-if="isMyBooth">Gian hàng của bạn</span>
                <span v-else-if="isAllSelectedBooksInCart">
                  {{ isAllBooksSelected ? 'Combo đã có trong giỏ' : 'Các cuốn đã chọn đã có trong giỏ' }}
                </span>
                <span v-else-if="isAllBooksSelected">Thêm trọn bộ Combo vào giỏ</span>
                <span v-else>Thêm {{ unaddedSelectedBooksCount }} cuốn vào giỏ</span>
              </button>
            </div>
          </div>

          <div class="book-items-grid">
            <div 
              v-for="(book, index) in booth.books" 
              :key="book.id || index" 
              class="book-item-card"
              :class="{ 'is-selected': isBookSelected(book, index) }"
            >
              <!-- Book Header: Checkbox + Number + Title + Catalog Link -->
              <div class="book-item-header">
                <!-- Checkbox for this book -->
                <label class="book-item-checkbox-wrap" @click.stop title="Tích chọn cuốn này để thêm vào giỏ hàng">
                  <input 
                    type="checkbox" 
                    class="custom-check-native" 
                    :checked="isBookSelected(book, index)"
                    @change="toggleBookSelection(book, index)"
                  />
                  <span class="custom-check-box">
                    <icon v-if="isBookSelected(book, index)" icon="fa-solid fa-check" class="check-icon" />
                  </span>
                </label>

                <div class="book-item-index">{{ index + 1 }}</div>
                <div class="book-item-title-group">
                  <div class="d-flex align-items-center gap-2 flex-wrap">
                    <h3 class="book-item-title font-serif">{{ book.name }}</h3>
                    <span v-if="isBookInCart(book, index)" class="book-in-cart-pill">
                      <icon icon="fa-solid fa-check" class="me-1" />Đã trong giỏ
                    </span>
                  </div>
                  <div class="book-item-meta">
                    <span v-if="book.author" class="item-meta-author">
                      <icon icon="fa-solid fa-feather-pointed" class="me-1" />{{ book.author }}
                    </span>
                    <span v-if="book.categoryNames && book.categoryNames.length > 0" class="item-meta-category">
                      <icon icon="fa-solid fa-tag" class="me-1" />{{ book.categoryNames.join(', ') }}
                    </span>
                  </div>
                </div>

                <router-link 
                  v-if="book.idBook" 
                  :to="'/book/' + book.idBook" 
                  class="btn-view-catalog-link"
                  title="Xem sách gốc trên hệ thống"
                  target="_blank"
                >
                  <icon icon="fa-solid fa-book-open" class="me-1" /> Sách gốc
                </router-link>
              </div>

              <!-- Book Body: 2 Photos (Front + Back) + Specs & Details -->
              <div class="book-item-body">
                <!-- 2 Photos Frame -->
                <div class="book-item-photos">
                  <!-- Front Cover -->
                  <div class="photo-box" @click="openZoomForBook(book.cover, book, 'book_' + index + '_cover')" title="Bấm phóng to bìa trước">
                    <img :src="getCoverUrl(book.cover)" alt="Bìa trước" @error="handleImageError" />
                    <span class="photo-box-label">Bìa trước</span>
                    <span class="photo-zoom-icon"><icon icon="fa-solid fa-magnifying-glass-plus" /></span>
                  </div>

                  <!-- Back Cover -->
                  <div class="photo-box" @click="openZoomForBook(book.coverBack, book, 'book_' + index + '_coverBack')" title="Bấm phóng to bìa sau">
                    <img :src="getCoverUrl(book.coverBack)" alt="Bìa sau" @error="handleImageError" />
                    <span class="photo-box-label">Bìa sau</span>
                    <span class="photo-zoom-icon"><icon icon="fa-solid fa-magnifying-glass-plus" /></span>
                  </div>
                </div>

                <!-- Book Details & Specs -->
                <div class="book-item-details">
                  <!-- Specs Grid -->
                  <div class="book-specs-strip">
                    <div class="spec-pill">
                      <span class="spec-lbl">Độ mới</span>
                      <strong class="spec-val text-emerald-600">{{ book.perStatus }}%</strong>
                    </div>
                    <div class="spec-pill">
                      <span class="spec-lbl">Tình trạng</span>
                      <strong class="spec-val" :class="'cond-text-' + book.verStatus">{{ getConditionLabel(book.verStatus) }}</strong>
                    </div>
                    <div class="spec-pill" v-if="book.totalPage > 0">
                      <span class="spec-lbl">Số trang</span>
                      <strong class="spec-val">{{ book.totalPage }}</strong>
                    </div>
                    <div class="spec-pill" v-if="book.originalPrice">
                      <span class="spec-lbl">Giá bìa</span>
                      <strong class="spec-val text-muted">{{ formatPrice(book.originalPrice) }}</strong>
                    </div>
                  </div>

                  <!-- Description -->
                  <div class="book-desc-block">
                    <span class="desc-lbl">Mô tả tình trạng:</span>
                    <p class="desc-txt">{{ book.description || 'Sách giữ gìn tốt.' }}</p>
                  </div>

                  <!-- Price & Single Buy Action (If allow single) -->
                  <div class="book-item-footer">
                    <div class="book-item-price-block">
                      <template v-if="booth.saleType === 0 && book.price">
                        <span class="price-lbl">Giá bán lẻ:</span>
                        <strong class="price-val">{{ formatPrice(book.price) }}</strong>
                      </template>
                      <template v-else>
                        <span class="price-combo-note">Thuộc bộ combo</span>
                      </template>
                    </div>

                    <button 
                      v-if="booth.saleType === 0 && book.price"
                      type="button" 
                      class="btn-buy-single"
                      :class="{ 'in-cart': isBookInCart(book, index), 'is-mine': isMyBooth }"
                      :disabled="book.status === 2 || booth.status === 2 || isBookInCart(book, index) || isMyBooth"
                      @click="handleBuySingleBook(book, index)"
                    >
                      <icon :icon="isMyBooth ? 'fa-solid fa-user-check' : (isBookInCart(book, index) ? 'fa-solid fa-check' : 'fa-solid fa-bag-shopping')" class="me-1" />
                      <span>{{ isMyBooth ? 'Sách của bạn' : (isBookInCart(book, index) ? 'Đã trong giỏ' : 'Mua lẻ cuốn này') }}</span>
                    </button>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </section>

      </div>
    </main>

    <!-- Zoom Full Image Modal (Interactive) -->
    <div v-if="showZoomModal" class="zoom-overlay" @click="closeZoomModal" @wheel.prevent="handleWheelZoom">
      <div class="zoom-toolbar" :class="{ 'zoom-toolbar-hidden': toolbarHidden }" @click.stop>
        <button class="zoom-tool-btn" @click="zoomOut" title="Thu nhỏ (-)" :disabled="zoomScale <= 0.5">
          <icon icon="fa-solid fa-minus" />
        </button>
        <span class="zoom-percent-badge">{{ Math.round(zoomScale * 100) }}%</span>
        <button class="zoom-tool-btn" @click="zoomIn" title="Phóng to (+)" :disabled="zoomScale >= 4">
          <icon icon="fa-solid fa-plus" />
        </button>
        <div class="zoom-tool-divider"></div>
        <button class="zoom-tool-btn" @click="resetZoom" title="Đặt lại kích thước (100%)">
          <icon icon="fa-solid fa-rotate-left" />
        </button>
        <button class="zoom-tool-btn close" @click="closeZoomModal" title="Đóng cửa sổ">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div 
        ref="zoomStage"
        class="zoom-content-stage" 
        @click.stop
        @dblclick="toggleDblZoom"
        @mousedown="startDrag"
        @mousemove="onDrag"
        @mouseup="stopDrag"
        @mouseleave="stopDrag"
      >
        <img 
          :src="getCoverUrl(activeImage)" 
          :alt="booth ? booth.name : ''" 
          @error="handleImageError" 
          :style="{
            transform: 'scale(' + zoomScale + ') translate(' + (zoomPanX / zoomScale) + 'px, ' + (zoomPanY / zoomScale) + 'px)',
            cursor: zoomScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in'
          }"
          :class="['zoom-image-element', { 'zoom-dragging': isDragging }]"
        />
      </div>

      <div class="zoom-hint-pill">
        <icon icon="fa-solid fa-circle-info" class="me-1" /> Lăn chuột hoặc phím + / - để phóng to · Nhấn đúp để phóng 200% · Kéo chuột để di chuyển ảnh · Esc để đóng
      </div>
    </div>

    <!-- Contact Seller / Order Modal -->
    <div v-if="showContactModal" class="modal-overlay" @click="showContactModal = false">
      <div class="modal-box" @click.stop>
        <div class="modal-header">
          <h3 class="font-serif">Liên hệ đặt mua sách cũ</h3>
          <button class="modal-close" @click="showContactModal = false"><icon icon="fa-solid fa-xmark" /></button>
        </div>
        
        <form @submit.prevent="submitContactMessage">
          <!-- Buying Target Info -->
          <div class="modal-target-card">
            <span class="target-type-badge">
              {{ selectedBookForContact ? 'Mua lẻ cuốn sách:' : 'Mua gian hàng / Combo:' }}
            </span>
            <h4 class="target-title">
              {{ selectedBookForContact ? selectedBookForContact.name : booth.name }}
            </h4>
            <div class="target-price">
              Giá: <strong>{{ formatPrice(selectedBookForContact ? selectedBookForContact.price : booth.price) }}</strong>
            </div>
          </div>

          <div class="modal-field">
            <label>Số điện thoại / Zalo của bạn <span class="req">*</span></label>
            <input type="text" v-model="contactPhone" required placeholder="Nhập SĐT để người bán liên hệ lại với bạn..." />
          </div>

          <div class="modal-field">
            <label>Địa chỉ nhận sách (tùy chọn)</label>
            <input type="text" v-model="contactAddress" placeholder="Nhập địa chỉ giao hàng..." />
          </div>

          <div class="modal-field">
            <label>Lời nhắn cho người bán</label>
            <textarea v-model="contactNote" rows="3" placeholder="Ví dụ: Tôi muốn mua cuốn sách này, vui lòng liên hệ Zalo cho tôi..."></textarea>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn-modal-cancel" @click="showContactModal = false">Hủy</button>
            <button type="submit" class="btn-modal-submit" :disabled="isSendingContact">
              {{ isSendingContact ? 'Đang gửi...' : 'Gửi thông tin đặt mua' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script src="./old-book-detail.js"></script>
<style scoped src="./old-book-detail.css"></style>
